/**
 * ADR-0210 Slice 3 — Demand tab: what agents asked for versus the
 * catalog, across all agents in the window. Four panels: products agents
 * ask for, refused demand by reason (with an estimated value at the
 * current catalog price — labelled as an estimate), products requested
 * while unavailable, and searches the store does not carry. Derived
 * from the daily rollups; never signed records.
 */

import { AlertTriangle, FileText, Package, Search, ShieldOff } from 'lucide-react';

import type { AgentDemandResponse, AgentKind, DemandProductRow } from '@sill/types/api';

import { EmptyStateCard } from '../../components/atoms/EmptyStateCard.js';
import { useC } from '../../lib/theme.js';
import { KIND_WORDS, PAGE_KIND_LABEL, fmtInt, fmtMoney, fmtPct, monoFont, rangePhrase, sansFont } from './format.js';
import type { AgentRollupRange } from '@sill/types/api';

function Kicker({ children }: { children: string }): JSX.Element {
  const C = useC();
  return <div style={{ fontFamily: monoFont, fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: C.muted, marginBottom: 10 }}>{children}</div>;
}

function Panel({ title, sub, children }: { title: string; sub?: string | undefined; children: React.ReactNode }): JSX.Element {
  const C = useC();
  return (
    <div style={{ padding: 16, border: `1px solid ${C.border}`, borderRadius: 2, background: C.surface }}>
      <Kicker>{title}</Kicker>
      {sub !== undefined ? <div style={{ fontFamily: sansFont, fontSize: 12, color: C.muted, marginTop: -4, marginBottom: 12 }}>{sub}</div> : null}
      {children}
    </div>
  );
}

function Nothing({ text }: { text: string }): JSX.Element {
  const C = useC();
  return <div style={{ fontFamily: sansFont, fontSize: 12, color: C.dim }}>{text}</div>;
}

function availabilityLabel(a: string | null): string {
  if (a === null) return '—';
  return a.replace(/_/g, ' ');
}

function ProductsTable({ rows, showUnavailable }: { rows: readonly DemandProductRow[]; showUnavailable: boolean }): JSX.Element {
  const C = useC();
  const th = (label: string, right = true): JSX.Element => (
    <th key={label} style={{ textAlign: right ? 'right' : 'left', padding: '6px 8px', fontWeight: 500, whiteSpace: 'nowrap' }}>{label}</th>
  );
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: sansFont, fontSize: 12.5 }}>
        <thead>
          <tr style={{ fontFamily: monoFont, fontSize: 9, letterSpacing: 1.5, color: C.muted, textTransform: 'uppercase' }}>
            {th('Product', false)}{th('Asked by', false)}{th('Views')}{th('Requested')}{th('Approved')}{th('Refused')}{showUnavailable ? th('Unavailable') : null}{th('Revenue')}
          </tr>
        </thead>
        <tbody>
          {rows.map((p) => (
            <tr key={p.catalog_product_id} style={{ borderTop: `1px solid ${C.borderSoft}` }} data-product-id={p.catalog_product_id}>
              <td style={{ padding: '8px', color: C.text }}>
                <div>{p.title ?? p.catalog_product_id}</div>
                <div style={{ fontFamily: monoFont, fontSize: 9, letterSpacing: 1, color: p.availability === 'in_stock' ? C.muted : C.amberFg, textTransform: 'uppercase', marginTop: 2 }}>
                  {availabilityLabel(p.availability)}{p.price_minor !== null ? ` · ${fmtMoney(p.price_minor, p.currency)}` : ''}
                </div>
              </td>
              <td style={{ padding: '8px', color: C.muted, fontSize: 12 }}>
                {p.top_agents.length === 0 ? '—' : p.top_agents.map((a) => `${a.name ?? 'Unidentified'} (${fmtInt(a.requested_orders > 0 ? a.requested_orders : a.views)})`).join(', ')}
              </td>
              <td style={{ padding: '8px', textAlign: 'right', fontFamily: monoFont, color: C.text }}>{fmtInt(p.views)}</td>
              <td style={{ padding: '8px', textAlign: 'right', fontFamily: monoFont, color: C.text }}>{fmtInt(p.requested_orders)}</td>
              <td style={{ padding: '8px', textAlign: 'right', fontFamily: monoFont, color: C.limeFg }}>{fmtInt(p.approved_orders)}</td>
              <td style={{ padding: '8px', textAlign: 'right', fontFamily: monoFont, color: p.rejected_orders > 0 ? C.coralFg : C.muted }}>{fmtInt(p.rejected_orders)}</td>
              {showUnavailable ? <td style={{ padding: '8px', textAlign: 'right', fontFamily: monoFont, color: C.amberFg }}>{fmtInt(p.unavailable_when_requested)}</td> : null}
              <td style={{ padding: '8px', textAlign: 'right', fontFamily: monoFont, color: C.text }}>{fmtMoney(p.revenue_minor, p.currency)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function Demand({ data, kind = 'all', range = '30d' }: { data: AgentDemandResponse; kind?: AgentKind | undefined; range?: AgentRollupRange | undefined }): JSX.Element {
  const C = useC();
  const who = KIND_WORDS[kind].plural;
  const when = rangePhrase(range);
  const total_requested = data.products.reduce((s, p) => s + p.requested_orders, 0);
  const total_refused_requests = data.refused.reduce((s, r) => s + r.requests, 0);
  const total_est_value = data.refused.reduce((s, r) => s + r.est_value_minor, 0);
  const currency = data.products.find((p) => p.currency !== null)?.currency ?? null;
  const has_pages = data.top_pages.length > 0;
  const nothing = data.products.length === 0 && data.refused.length === 0 && data.searched_all.length === 0 && !has_pages;
  const commerce = data.has_catalog || data.products.length > 0 || data.refused.length > 0;
  const kind_total = Object.values(data.pages_by_kind).reduce((s, v) => s + v, 0);
  const kinds = Object.entries(data.pages_by_kind).sort((a, b) => b[1] - a[1]);
  const path_max = Math.max(1, ...data.top_pages.map((p) => p.n));

  if (nothing) {
    return (
      <EmptyStateCard
        icon={Package}
        label={`No demand from ${who} ${when}`}
        hint={kind === 'bots'
          ? 'Crawlers and tools rarely search or request products; switch to AI agents to see what assistants ask for. Rollups refresh every 15 minutes.'
          : `Demand appears once ${who} load pages, search your store, or request products. Rollups refresh every 15 minutes; page-level detail is kept from 2026-09-22 forward.`}
      />
    );
  }

  return (
    <div data-testid="demand">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2" style={{ marginBottom: 16 }}>
        {(commerce ? [
          ['Products asked for', fmtInt(data.products.length)],
          ['Requests', fmtInt(total_requested)],
          ['Refused requests', fmtInt(total_refused_requests)],
          ['Refused value (est.)', total_est_value > 0 ? fmtMoney(total_est_value, currency) : '—'],
        ] : [
          ['Page loads', fmtInt(kind_total)],
          ['Pages read', fmtInt(data.top_pages.length)],
          ['Searches', fmtInt(data.searched_all.reduce((s, t) => s + t.n, 0))],
          ['Product pages', fmtInt(data.pages_by_kind['product'] ?? 0)],
        ]).map(([label, value]) => (
          <div key={label} style={{ padding: '12px 14px', border: `1px solid ${C.border}`, borderRadius: 2, background: C.surface }}>
            <div style={{ fontFamily: monoFont, fontSize: 9, letterSpacing: 1.5, textTransform: 'uppercase', color: C.muted }}>{label}</div>
            <div style={{ fontFamily: monoFont, fontSize: 20, fontWeight: 600, color: C.text, marginTop: 2, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gap: 12 }}>
        <Panel title={`What ${who} read`} sub={`The pages ${who} loaded most, and who loaded them. On a site without a catalog this is the demand: which content they come for.`}>
          {!has_pages ? <Nothing text={`No page loads from ${who} ${when}.`} /> : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2" style={{ display: 'flex', flexDirection: 'column', gap: 6 }} data-testid="top-pages">
                {data.top_pages.map((p) => (
                  <div key={p.path} style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', gap: 10, alignItems: 'center' }}>
                    <div style={{ minWidth: 0 }}>
                      <div className="flex items-center gap-1.5" style={{ fontFamily: monoFont, fontSize: 12, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <FileText size={11} color={C.muted} style={{ flexShrink: 0 }} /> {p.path}
                      </div>
                      <div style={{ height: 3, background: C.surface2, borderRadius: 2, marginTop: 3 }}>
                        <div style={{ height: 3, width: `${Math.max(2, Math.round((p.n / path_max) * 100))}%`, background: C.limeFg, borderRadius: 2 }} />
                      </div>
                      <div style={{ fontFamily: sansFont, fontSize: 11, color: C.muted, marginTop: 3 }}>
                        {p.top_agents.map((a) => `${a.name ?? 'Unidentified'} (${fmtInt(a.n)})`).join(' · ')}
                      </div>
                    </div>
                    <div style={{ fontFamily: monoFont, fontSize: 12, color: C.text, fontVariantNumeric: 'tabular-nums' }}>{fmtInt(p.n)}</div>
                  </div>
                ))}
              </div>
              <div>
                <Kicker>By page type</Kicker>
                {kinds.length === 0 ? <Nothing text="—" /> : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {kinds.map(([k, n]) => (
                      <div key={k} style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: 8, alignItems: 'center', fontFamily: sansFont, fontSize: 12.5, color: C.text }}>
                        <span>{PAGE_KIND_LABEL[k] ?? k}</span>
                        <span style={{ fontFamily: monoFont, fontSize: 11, color: C.muted }}>{fmtPct(n, kind_total)}</span>
                        <span style={{ fontFamily: monoFont, fontSize: 12, fontVariantNumeric: 'tabular-nums', minWidth: 40, textAlign: 'right' }}>{fmtInt(n)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </Panel>

        {commerce ? (
        <Panel title={`Products ${who} ask for`} sub={`Product pages ${who} loaded, and the SKUs they requested through governed checkout.`}>
          {data.products.length === 0
            ? <Nothing text={data.has_catalog ? `No product views or requests from ${who} matched your catalog ${when}.` : 'Sync a catalog (Catalog tab) to resolve page loads and requests to products.'} />
            : <ProductsTable rows={data.products} showUnavailable={false} />}
        </Panel>
        ) : (
          <div style={{ fontFamily: sansFont, fontSize: 12, color: C.muted, padding: '2px 2px' }}>
            Product demand, refused requests and out-of-stock asks appear once a catalog is synced and agents can place governed orders. For a site without a store, the pages above are the demand.
          </div>
        )}

        {commerce ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Panel title="Refused demand" sub={data.est_value_note}>
            {data.refused.length === 0 ? <Nothing text={`No requests from ${who} were refused ${when}.`} /> : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {data.refused.map((r) => (
                  <div key={r.reason} style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 12, alignItems: 'baseline' }} data-reason={r.reason}>
                    <div>
                      <div className="flex items-center gap-1.5" style={{ fontFamily: sansFont, fontSize: 12.5, color: C.text }}>
                        <ShieldOff size={11} color={C.coralFg} /> {r.reason.replace(/_/g, ' ')}
                      </div>
                      <div style={{ fontFamily: monoFont, fontSize: 9, letterSpacing: 1, color: C.muted, marginTop: 2 }}>
                        {fmtInt(r.requests)} REQUEST{r.requests === 1 ? '' : 'S'}{r.product_lines > 0 ? ` · ${fmtInt(r.product_lines)} PRODUCT LINE${r.product_lines === 1 ? '' : 'S'}` : ''}
                      </div>
                    </div>
                    <div style={{ fontFamily: monoFont, fontSize: 13, color: r.est_value_minor > 0 ? C.text : C.muted, fontVariantNumeric: 'tabular-nums' }}>
                      {r.est_value_minor > 0 ? `≈ ${fmtMoney(r.est_value_minor, currency)}` : '—'}
                    </div>
                  </div>
                ))}
                <div style={{ fontFamily: sansFont, fontSize: 11, color: C.dim, marginTop: 4 }}>
                  Refusals are your guardrails working. If a reason surprises you, review the rule in Rules.
                </div>
              </div>
            )}
          </Panel>

          <Panel title="Requested while unavailable" sub="Agents asked for these while they were out of stock or not exposed to agents.">
            {data.unavailable.length === 0 ? <Nothing text={`Nothing requested by ${who} while unavailable ${when}.`} /> : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {data.unavailable.map((p) => (
                  <div key={p.catalog_product_id} style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 12, alignItems: 'baseline' }}>
                    <div className="flex items-center gap-1.5" style={{ fontFamily: sansFont, fontSize: 12.5, color: C.text }}>
                      <AlertTriangle size={11} color={C.amberFg} /> {p.title ?? p.catalog_product_id}
                      <span style={{ fontFamily: monoFont, fontSize: 9, letterSpacing: 1, color: C.amberFg, textTransform: 'uppercase' }}>{availabilityLabel(p.availability)}</span>
                    </div>
                    <div style={{ fontFamily: monoFont, fontSize: 12, color: C.text }}>{fmtInt(p.unavailable_when_requested)} of {fmtInt(p.requested_orders)}</div>
                  </div>
                ))}
              </div>
            )}
          </Panel>
        </div>
        ) : null}

        <Panel
          title={data.has_catalog ? 'Searched, not sold' : `What ${who} search for`}
          sub={data.has_catalog ? `Search terms from ${who} with no match in your catalog titles or SKUs.` : 'Sync a catalog to see which of these you do not carry.'}
        >
          {(data.has_catalog ? data.searched_unsold : data.searched_all).length === 0
            ? <Nothing text={data.has_catalog ? `Every search from ${who} ${when} matched something in your catalog.` : `No searches from ${who} ${when}. Searches come from search pages and from AI agents' browse and recommend calls.`} />
            : (
              <div className="flex flex-wrap gap-2">
                {(data.has_catalog ? data.searched_unsold : data.searched_all).map((t) => (
                  <span key={t.term} className="inline-flex items-center gap-1.5 px-2 py-1" style={{ border: `1px solid ${C.border}`, borderRadius: 2, fontFamily: sansFont, fontSize: 12, color: C.text, background: C.surface2 }}>
                    <Search size={10} color={C.muted} /> {t.term}
                    <span style={{ fontFamily: monoFont, fontSize: 10, color: C.muted }}>{fmtInt(t.n)}</span>
                  </span>
                ))}
              </div>
            )}
        </Panel>

        {data.products.length > 0 ? (
          <div style={{ fontFamily: monoFont, fontSize: 9, letterSpacing: 1.5, color: C.dim, textTransform: 'uppercase' }}>
            Approval across products · {fmtPct(data.products.reduce((s, p) => s + p.approved_orders, 0), data.products.reduce((s, p) => s + p.approved_orders + p.rejected_orders, 0))}
          </div>
        ) : null}
      </div>
    </div>
  );
}
